<template>
  <DatatableReportV3
    v-if="enabled && pageData"
    title="Phone Calls"
    :no-download="true"
    :source="pageData.source"
    :filters="pageData.filters"
    :config="config"
    data-cy="admin-calls-space"
  />
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useResolvedFeatureFlag } from "@/lib/useFeatureFlag";

import CallNoteLabel from "@/components/CallNoteLabel.vue";
import { Filter } from "@/components/DatatableV3.vue";
import SecondsLabel from "@/components/SecondsLabel.vue";
import CallModalLink from "@/CallComponents/CallModalLink.vue";
import CallNoteLink from "@/CallComponents/CallNoteLink.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import store from "@/vuex/store";

interface PageData {
  source: string;
  filters: Filter[];
}

const COLUMNS = [
  { data: "direction", title: "Direction", orderable: false },
  { data: "call_id", title: "Call ID" },
  { data: "call_status", title: "Status", orderData: 3 },
  { data: "call_status_alpha", visible: false, searchable: false },
  {
    data: "start_time",
    title: "Start Time",
    filterName: "start_date",
    searchable: false
  },
  { data: "answered", title: "Answered" },
  {
    data: "duration",
    title: "Duration",
    className: "text-right",
    searchable: false
  },
  {
    data: "ended_at",
    title: "End Time",
    searchable: false,
    filterName: "ended_at"
  },
  { data: "claimed_by", title: "Assigned To", filterName: "assigned_to" },
  { data: "caller_id", title: "Caller ID" },
  { data: "phone", title: "Phone #" },
  { data: "to_phone", title: "To", searchable: false },
  // Filtered with the Provider dropdown; the stored value is not searchable text.
  {
    data: "provider",
    title: "Provider",
    orderable: false,
    searchable: false
  },
  { data: "call_source", title: "Call Source" },
  {
    data: "linked_job",
    title: "Linked Job",
    orderable: false,
    searchable: false
  },
  {
    data: "linked",
    title: "Linked",
    className: "text-right",
    searchable: false
  },
  {
    data: "call_revenue_cents",
    title: "Call Revenue",
    className: "text-right",
    searchable: false
  },
  { data: "call_note", title: "Note", orderable: false, searchable: false }
];

const START_TIME_COLUMN = COLUMNS.findIndex(
  (column) => column.data === "start_time"
);

const config = {
  // Newest calls first.
  order: [[START_TIME_COLUMN, "desc"]],
  columns: COLUMNS,
  vueComponents: [
    { rootId: "seconds-label-vue", component: SecondsLabel },
    {
      rootId: "call-modal-link-vue",
      component: CallModalLink,
      options: { store }
    },
    {
      rootId: "call-notes-modal-vue",
      component: CallNoteLink,
      options: { store }
    },
    { rootId: "call-note-tooltip", component: CallNoteLabel }
  ]
};

const router = useRouter();
const { enabled, resolved } = useResolvedFeatureFlag(
  "dialpad_integration_ff_enabled"
);
const pageData = ref<PageData | null>(null);

// The menu hides the space while Dialpad is off; a direct visit lands here.
watch(
  [resolved, enabled],
  ([isResolved, isEnabled]) => {
    if (isResolved && !isEnabled) router.replace({ name: "Unauthorized" });
  },
  { immediate: true }
);

watch(
  enabled,
  async (isEnabled) => {
    if (!isEnabled || pageData.value) return;

    const res = await fetch(`${window.location.pathname}/initial_page_data`, {
      method: "GET",
      headers: { Accept: "application/json" }
    });
    if (res.ok) pageData.value = await res.json();
  },
  { immediate: true }
);
</script>
